import { Result } from '@easeltv/fail';
import { useCallback, useEffect, useState } from 'react';

import { useConfig } from '../../../hooks/useConfig/useConfig';
import { useDictionary } from '../../../hooks/useDictionary/useDictionary';
import { useLogoImage } from '../../../hooks/useLogoImage/useLogoImage';
import { containsOnlyLetters } from '../../../utilities';
import DumbTokenSection from './TokenSection.dumb';

/* ---------------------------------- Types --------------------------------- */

/**
 * TokenSectionI
 */
export interface TokenSectionI {
    /** number of input fields */
    numberOfInputs: number;

    validateToken: (token: string) => Promise<Result<object>>;

    externalError?: string;
}

/* -------------------------------- Component ------------------------------- */

/**
 * TokenSection
 *
 * Renders a row of input fields used to submit token inputs.
 *
 * @param props TokenSectionI
 * @returns JSX.Element
 */
const TokenSection = ({
    numberOfInputs,
    validateToken,
    externalError,
}: TokenSectionI) => {
    const [error, setError] = useState(externalError);
    const [currentIndex, setCurrentIndex] = useState(0);

    const logo = useLogoImage(300, 73, 'page');

    useEffect(() => {
        setError(externalError);
    }, [externalError]);

    const {
        device: {
            web: { siteType },
        },
    } = useConfig();

    const [isBusy, setIsBusy] = useState(false);
    const [token, setToken] = useState<string[]>(
        Array.from({ length: numberOfInputs }, () => ''),
    );

    const labels = {
        pairingTitleText: useDictionary('pairing.title'),
        pairingBusyText: useDictionary('pairing.busy'),
        pairingExitText: useDictionary('pairing.exit'),
    };

    const badCharacterText = useDictionary('pairing.invalid-character');
    const invalidOrExpiredCodeText = useDictionary('pairing.expired');

    const resetForm = useCallback(() => {
        setToken(Array.from({ length: numberOfInputs }, () => ''));
        setIsBusy(false);
        setCurrentIndex(0);
    }, [numberOfInputs]);

    const handleChange = (index: number) => (character: string) => {
        setError(undefined);

        if (containsOnlyLetters(character) || character === '') {
            setToken((passedToken) => {
                const newToken = [...passedToken];
                newToken[index] = character;
                return newToken;
            });

            if (character !== '') {
                if (index < numberOfInputs - 1) {
                    setCurrentIndex(index + 1);
                }
            }
        } else {
            setError(badCharacterText);
        }
    };

    const validate = useCallback(
        async (passedToken: string) => {
            const result = await validateToken(passedToken);
            if (!result.ok) {
                resetForm();
                setError(invalidOrExpiredCodeText);
            }
        },
        [validateToken, invalidOrExpiredCodeText, resetForm],
    );

    // handles moving to the previous field
    const moveToPreviousField = (index: number) => {
        if (index !== 0) {
            setCurrentIndex(index - 1);
        }
    };

    // handles when a token is pasted
    const handlePaste = () => {
        navigator.clipboard
            .readText()
            .then((pastedToken) => {
                const trimmedString = pastedToken.substring(0, numberOfInputs);
                setToken(trimmedString.split(''));
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('Failed to read clipboard contents: ', err);
            });
    };

    // make the request when token meets criteria
    useEffect(() => {
        if (token.join('').trim().length === numberOfInputs) {
            validate(token.join('').trim());
        }
    }, [token, numberOfInputs, validate]);

    return (
        <DumbTokenSection
            token={token}
            error={error}
            labels={labels}
            isBusy={isBusy}
            handlePaste={handlePaste}
            handleChange={handleChange}
            currentIndex={currentIndex}
            logo={logo}
            moveToPrevious={moveToPreviousField}
            showExitButton={siteType === 'site'}
        />
    );
};

/* --------------------------------- Export --------------------------------- */

export default TokenSection;
